import { $, CustomEventWithDetail, html, rune, View } from 'rune-ts';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import videojs, { type VideoJsPlayer, type VideoJsPlayerOptions } from 'video.js';
import 'videojs-youtube';
import { PlayIcon } from '../../atoms/Icon';
import klass from './Video.module.scss';
import video_klass from './Video.scss';

export interface VideoData {
  id: number | string;
  src: string;
  thumbnail?: string | null;
}
type VideoOption = {
  klass?: string;
  format: 'youtube' | 'video';
} & VideoJsPlayerOptions;

export class VideoControlBarEvent extends CustomEventWithDetail<'enter' | 'leave'> {}
export class VideoActiveEvent extends CustomEventWithDetail<{ id: number | string; active: boolean }> {}

export class Video extends View<VideoData> {
  option: VideoOption;
  player?: VideoJsPlayer;
  play_button$?: HTMLElement;
  thumbnail$?: HTMLElement;

  constructor(data: VideoData, option: VideoOption) {
    super(data, option);

    this.option = option;
  }

  override template() {
    const { src, thumbnail } = this.data;

    return html`<div class="${this.option.klass ?? ''}">
      <video class="${klass.video} video-js vjs-default-skin" src="${src}" playsinline></video>
      <div class="thumbnail ${klass.thumbnail}">
        ${thumbnail ? html`<img src="${thumbnail}" alt="thumbnail" />` : ''}
        <div class="play ${klass.play}">${PlayIcon}</div>
      </div>
    </div> `;
  }

  protected override onRender() {
    const { autoplay, format } = this.option;
    const { src, id } = this.data;
    const this$ = $(this.element());
    const is_mobile = !!rune.getSharedData(this)?.is_mobile;
    const video$ = this$.find('.video-js')?.element();
    this.play_button$ = this$.find('.play')?.element();
    this.thumbnail$ = this$.find('.thumbnail')?.element();

    const option: VideoJsPlayerOptions = {
      playsinline: true,
      aspectRatio: '16:9',
      controls: true,
      loop: true,
      muted: true,
      inactivityTimeout: is_mobile ? 1000 : 2000,
      userActions: {
        click: format === 'video',
        doubleClick: false,
      },
      controlBar: {
        bigPlayButton: false,
        pictureInPictureToggle: false,
        fullscreenToggle: false,
        remainingTimeDisplay: {
          displayNegative: false,
        },
        progressControl: {
          seekBar: false,
        },
      },
    };

    if (format === 'youtube') {
      Object.assign(option, {
        techOrder: ['youtube'],
        sources: [{ type: 'video/youtube', src }],
        youtube: {
          enablePrivacyEnhancedMode: true,
          ytControls: 0,
          playerVars: {
            loop: 1,
            mute: 1,
            rel: 0,
            modestbranding: 1,
            showinfo: 0,
            playsinline: 1,
            iv_load_policy: 3,
            origin: location.href,
            widget_referrer: location.href,
            disablekb: 1,
          },
          customVars: {
            autoplay: autoplay ? 1 : 0,
          },
        },
      });
    } else {
      Object.assign(option, { src });
    }

    Object.assign(option, this.option);

    this.player = videojs(video$!, option);
    this.player.bigPlayButton.addClass(video_klass.vjs_hide);
    this.player.loadingSpinner.addClass(video_klass.vjs_hide);

    this.player.on('pause', this.playHandler(true));
    this.player.on('play', this.playHandler(false));

    this.player.on('useractive', () => {
      this.dispatchEvent(VideoActiveEvent, { bubbles: true, detail: { active: true, id } });
    });
    this.player.on('userinactive', () => {
      this.dispatchEvent(VideoActiveEvent, { bubbles: true, detail: { active: false, id } });
    });

    if (format === 'youtube') {
      this.player.on('click', this.clickHandler);
    }

    this.delegate('click', '.thumbnail', () => {
      if (this.player?.paused()) {
        this.play();
      }
    });

    if (autoplay) {
      setTimeout(() => {
        this.player?.ready(() => this.play());
      }, 100);
    }

    this.play_button$?.addEventListener('animationend', () => {
      if (this.thumbnail$) {
        this.thumbnail$.style.display = this.player?.paused() ? '' : 'none';
      }
    });
  }

  play() {
    if (this.player) {
      const video_played = this.player?.play();

      if (video_played instanceof Promise) {
        video_played.catch(() => {});
      }
    }
  }

  pause() {
    if (this.player) {
      return this.player?.pause();
    }
  }

  paused() {
    if (this.player) {
      return this.player?.paused();
    }
    return true;
  }

  private thumbnailVisibleStart() {
    this.play_button$!.dataset.video_show = 'true';
    this.thumbnail$!.dataset.video_show = 'true';
    this.player?.userActive(true);
  }

  private thumbnailHiddenStart() {
    this.play_button$!.dataset.video_show = 'false';
    this.thumbnail$!.dataset.video_show = 'false';
    this.player?.userActive(false);
  }

  private playHandler = (paused: boolean) => () => {
    if (paused && this.thumbnail$) {
      this.thumbnail$!.style.display = '';
    }
    paused ? this.thumbnailVisibleStart() : this.thumbnailHiddenStart();
  };

  private clickHandler = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    const is_click_origin = event.currentTarget === target || $(target).hasClass('vjs-iframe-blocker');

    const is_mobile = !!rune.getSharedData(this)?.is_mobile;

    if (is_click_origin) {
      if (!this.player?.userActive() && !this.paused()) {
        this.player?.userActive(true);
        return;
      }

      if (this.player?.userActive()) {
        this.player?.userActive(false);
        if (!is_mobile) this.player?.pause();
      }
    }
  };
}
